import { ColorTrait } from "@/data/colors";

export interface InvitePayload {
  /** Name of the sender's personality archetype, e.g. "The Quiet Flame". */
  title: string;
  /** The 3 dominant color ids from the sender's personality result. */
  colorIds: string[];
}

const INVITE_PARAM = "with";

/** Base64url-encode a small JSON payload for a shareable URL query param.
 *  No backend involved — the entire invite round-trips through the link
 *  itself, so it works even though this is a static, account-free site. */
export function encodeInvite(payload: InvitePayload): string {
  const json = JSON.stringify(payload);
  const b64 = typeof window !== "undefined" ? window.btoa(unescape(encodeURIComponent(json))) : "";
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeInvite(encoded: string): InvitePayload | null {
  try {
    const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    const json = decodeURIComponent(escape(window.atob(padded)));
    const parsed = JSON.parse(json);
    if (typeof parsed?.title === "string" && Array.isArray(parsed?.colorIds)) {
      return { title: parsed.title, colorIds: parsed.colorIds.filter((x: unknown) => typeof x === "string") };
    }
    return null;
  } catch {
    return null;
  }
}

export function buildInviteUrl(payload: InvitePayload): string {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  url.search = "";
  url.searchParams.set(INVITE_PARAM, encodeInvite(payload));
  return url.toString();
}

export function readInviteFromLocation(): InvitePayload | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(INVITE_PARAM);
  return raw ? decodeInvite(raw) : null;
}

const SYNERGY_PARAM = "p1";

/** Builds a shareable `/synergy?p1=<hex>` URL from a user's dominant quiz
 *  color — the simple, human-readable counterpart to the base64 `?with=`
 *  invite used on the personality quiz result screen. */
export function buildSynergyShareUrl(hex: string): string {
  if (typeof window === "undefined") return "";
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const clean = hex.replace("#", "").toUpperCase();
  return `${window.location.origin}${base}/synergy?${SYNERGY_PARAM}=${clean}`;
}

/** Reads the sender's primary color hex from a `/synergy?p1=<hex>` link. */
export function readSynergyPrimaryFromLocation(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get(SYNERGY_PARAM);
  if (!raw) return null;
  return /^[0-9a-fA-F]{6}$/.test(raw) ? `#${raw.toUpperCase()}` : null;
}

export interface SynergyResult {
  score: number; // 0-100
  label: string;
  description: string;
}

/** Deterministic "Color Synergy" score between two personality readings,
 *  based on family overlap and keyword overlap of their dominant colors —
 *  no randomness, so the same two results always produce the same score. */
export function computeSynergy(
  mine: ColorTrait[],
  theirs: ColorTrait[],
  myTitle: string,
  theirTitle: string
): SynergyResult {
  const familyOverlap = mine.filter((m) => theirs.some((t) => t.family === m.family)).length;
  const keywordOverlap = new Set(
    mine.flatMap((m) => m.keywords).filter((kw) => theirs.some((t) => t.keywords.includes(kw)))
  ).size;

  const familyScore = (familyOverlap / mine.length) * 60; // up to 60
  const keywordScore = Math.min(keywordOverlap, 4) * 10; // up to 40
  const score = Math.round(Math.min(100, familyScore + keywordScore));

  let label: string;
  let description: string;
  if (score >= 75) {
    label = "Strong Synergy";
    description = `"${myTitle}" and "${theirTitle}" pull from very similar emotional territory — you likely read situations, and each other, in compatible ways.`;
  } else if (score >= 45) {
    label = "Balanced Synergy";
    description = `"${myTitle}" and "${theirTitle}" share some common ground but bring different instincts to the table — a mix that usually balances rather than clashes.`;
  } else {
    label = "Contrast Synergy";
    description = `"${myTitle}" and "${theirTitle}" come from noticeably different color territory — you likely see things from angles the other wouldn't think to check.`;
  }

  return { score, label, description };
}
